import { useEffect, useState, type ChangeEvent } from 'react';
import { Input } from '@klaro7/ui';

/**
 * Campo data di nascita digitabile (gg/mm/aaaa).
 *
 * `<input type="date">` su Android apre solo il calendario nativo, che parte
 * dal mese corrente: per una data di nascita significa decine di tap e nessuna
 * possibilità di scrivere. Qui usiamo un campo di testo con tastiera numerica
 * e slash inseriti in automatico, mentre verso l'esterno il valore resta in
 * formato ISO `YYYY-MM-DD` come prima.
 */

const MIN_YEAR = 1900;

/** ISO `YYYY-MM-DD` → `gg/mm/aaaa` (stringa vuota se il formato non torna). */
function isoToDisplay(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

/** Cifre grezze → `gg`, `gg/mm`, `gg/mm/aaaa` a seconda di quante ne sono state digitate. */
function digitsToDisplay(digits: string): string {
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
}

/**
 * `gg/mm/aaaa` → ISO `YYYY-MM-DD`, oppure null se la data non è completa o non
 * esiste sul calendario (31/02, mese 13, anno assurdo...).
 */
export function displayToIso(display: string): string | null {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(display);
  if (!m) return null;

  const day = Number(m[1]);
  const month = Number(m[2]);
  const year = Number(m[3]);

  if (month < 1 || month > 12) return null;
  if (year < MIN_YEAR || year > new Date().getFullYear()) return null;

  // Roundtrip: JS normalizza il 31/02 in 03/03, quindi se i pezzi non tornano
  // la data non esiste.
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }

  return `${m[3]}-${m[2]}-${m[1]}`;
}

export interface DateOfBirthInputProps {
  label?: string;
  /** Data in formato ISO `YYYY-MM-DD`, o stringa vuota. */
  value: string;
  /** Riceve la data ISO, o stringa vuota finché non è valida e completa. */
  onChange: (iso: string) => void;
  /** Errore del form padre (es. "Devi avere almeno 18 anni"): ha la precedenza. */
  error?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  name?: string;
  id?: string;
  /** Classi extra sull'input (le pagine con styling proprio, es. profilo fiscale). */
  className?: string;
}

export function DateOfBirthInput({
  label = 'Data di nascita',
  value,
  onChange,
  error,
  helperText,
  required,
  disabled,
  name,
  id,
  className,
}: DateOfBirthInputProps) {
  const [text, setText] = useState(() => isoToDisplay(value));
  const [touched, setTouched] = useState(false);

  // Il padre può cambiare il valore dall'esterno (caricamento profilo, reset
  // del form): riallineiamo il testo solo se rappresenta un'altra data.
  useEffect(() => {
    setText((current) => (displayToIso(current) === (value || null) ? current : isoToDisplay(value)));
  }, [value]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;

    // Autofill del browser (o incolla): arriva la data già in ISO.
    const iso = isoToDisplay(raw);
    if (iso) {
      setText(iso);
      onChange(displayToIso(iso) ?? '');
      return;
    }

    let digits = raw.replace(/\D/g, '').slice(0, 8);

    // Backspace su uno slash: l'utente non ha tolto cifre, quindi la togliamo noi.
    if (raw.length < text.length && digits === text.replace(/\D/g, '')) {
      digits = digits.slice(0, -1);
    }

    const next = digitsToDisplay(digits);
    setText(next);
    onChange(displayToIso(next) ?? '');
  };

  const incomplete = touched && text.length > 0 && displayToIso(text) === null;
  const shownError = error ?? (incomplete ? 'Data non valida: usa il formato gg/mm/aaaa' : undefined);

  return (
    <Input
      label={label}
      type="text"
      inputMode="numeric"
      autoComplete="bday"
      placeholder="gg/mm/aaaa"
      maxLength={10}
      name={name}
      id={id}
      value={text}
      onChange={handleChange}
      onBlur={() => setTouched(true)}
      required={required}
      disabled={disabled}
      error={shownError}
      helperText={helperText}
      className={className}
    />
  );
}
